import {inject, Injectable} from '@angular/core';
import {catchError, Observable, of, tap, throwError} from 'rxjs';

import {Wallet} from '../../domain/model/wallet.model';
import {
  InvalidQrCodeError,
  WalletError,
  WalletGenericError,
} from '../../domain/model/wallet.errors';

import {WalletRepository} from '@features/wallet/domain/repository/wallet.repository';
import {WalletStore} from '../store/wallet.store';

export interface ScanWalletQRRequest {
  qrCode: string;
}

@Injectable({providedIn: 'root'})
export class ScanWalletQRUseCase {
  private readonly repo = inject(WalletRepository);
  private readonly store = inject(WalletStore);

  execute({qrCode}: ScanWalletQRRequest): Observable<Wallet | null> {
    this.store.setLoading();

    return this.fetchWallet(qrCode).pipe(
      catchError(err => this.handleError(err)),
    );
  }

  private fetchWallet(qrCode: string): Observable<Wallet | null> {
    const clean = qrCode?.trim();

    if (!clean) {
      return throwError(() => new InvalidQrCodeError());
    }

    return this.repo.getWallet(clean).pipe(
      tap(wallet => this.store.setWalletFetched(wallet ?? undefined)));
  }

  private handleError(error: unknown): Observable<Wallet | null> {
    const mapped = this.toWalletError(error);

    this.store.setError(mapped.i18nKey);

    return of(null);
  }

  private toWalletError(error: unknown): WalletError {
    if (error instanceof WalletError) {
      return error;
    }

    return new WalletGenericError();
  }
}
